import { Intro } from '../../models';
import React, { useEffect, useMemo, useState } from 'react';
import {
  FieldHookConfig,
  Form,
  Formik,
  FormikErrors,
  useFormikContext,
} from 'formik';
import { nanoid } from 'nanoid';
import CustomField from '../fields/CustomField';
import CustomTextareaField from '../fields/CustomTextareaField';
import { TemplateImgField } from '../fields/TemplateImgField';
import { useTranslation } from 'react-i18next';

function ImageDescriptionField(
  props: { title: string; placeholder: string } & FieldHookConfig<string>
) {
  const { values } = useFormikContext<Intro>();
  return (
    <CustomField {...props} disabled={!values.image && !values.imageUrl} />
  );
}

export default function EditIntro({
  intro,
  onConfirm,
  onCancel,
}: {
  intro: Intro;
  onConfirm: (intro: Intro) => void;
  onCancel: () => void;
}) {
  const [t] = useTranslation(['admin', 'common']);
  const [invalid, setInvalid] = useState(false);

  const initialValues: Intro = useMemo(
    () => ({
      id: intro.id,
      title: intro.title,
      introduction: intro.introduction,
      image: intro.image,
      imageUrl: intro.imageUrl,
      imageDescription: intro.imageDescription,
      step: intro.step,
    }),
    [intro]
  );

  useEffect(() => {
    // Validate on initial values at first render
    validate(initialValues);
  }, [initialValues]);

  const validate = (values: Intro) => {
    const errors: FormikErrors<Intro> = {};
    if (!values.title) {
      errors.title = 'This value is required.';
    }
    if (!values.introduction) {
      errors.introduction = 'This value is required.';
    }

    setInvalid(Object.keys(errors).length > 0);

    return errors;
  };

  const onSubmit = (values: Intro) => {
    const { image } = values;
    if (image) {
      const name = nanoid();
      values.image = new File([image], name, { type: image.type });
      values.imageName = name;
    }
    onConfirm(values);
  };

  return (
    <div className="min-h-[500px] min-w-[500px] shadow-regular rounded-xl bg-white p-8">
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        validate={validate}
      >
        <Form className="flex flex-col gap-4">
          <CustomField
            title={t('activity_intro_title', '互動內容標題')}
            placeholder=""
            name="title"
          />
          <CustomTextareaField
            title={t('activity_intro_description', '互動內容簡介')}
            placeholder=""
            name="introduction"
          />
          <TemplateImgField
            title={t('activity_intro_image_title', '上傳互動內容預覽圖')}
            name="image"
            imageUrl={intro.imageUrl}
          />
          <ImageDescriptionField
            title={t('activity_intro_image_description', '互動內容預覽圖敘述')}
            placeholder=""
            name="imageDescription"
          />

          <div className="flex justify-center gap-10">
            <button
              type="button"
              className="border border-group-primary text-group-primary py-3 px-8 rounded text-center disabled:opacity-30 disabled:cursor-not-allowed"
              onClick={onCancel}
            >
              {t('cancel', '取消', { ns: 'common' })}
            </button>
            <button
              type="submit"
              disabled={invalid}
              className="bg-group-primary text-white py-3 px-8 rounded text-center disabled:opacity-30 disabled:cursor-not-allowed"
            >
              {t('submit', '送出', { ns: 'common' })}
            </button>
          </div>
        </Form>
      </Formik>
    </div>
  );
}
